/*
 * TFX FAQ Media
 * Objective: FAQ accordion with optional tabs
 * Scope: sections/tfx-faq-media.liquid
 * Dependencies: none
 * Date: 2026-04-25
 * Rollback: remove this file and restore the inline script in the section.
 */
(function () {
  var INIT_KEY = '__tfxFaqMediaInit';
  if (window[INIT_KEY]) {
    window[INIT_KEY]();
    return;
  }

  var ROOT_SELECTOR   = '[data-tfx-faq-media]';
  var TAB_SELECTOR    = '.tfx-faq-media__tab';
  var TAB_PANEL_SELECTOR = '.tfx-faq-media__tab-panel';
  var ACCORDION_SELECTOR = '.tfx-accordion';
  var ITEM_SELECTOR   = '.tfx-accordion__item';
  var TOGGLE_SELECTOR = '.tfx-accordion__toggle';
  var PANEL_SELECTOR  = '.tfx-accordion__panel';
  var CONTENT_SELECTOR = '.tfx-faq-media__content';
  var supportsResizeObserver = typeof ResizeObserver === 'function';

  var reducedMotionQuery = window.matchMedia
    ? window.matchMedia('(prefers-reduced-motion: reduce)')
    : null;
  var prefersReducedMotion = function () {
    return !!(reducedMotionQuery && reducedMotionQuery.matches);
  };

  /* ----------------------------
     Height helpers
  ---------------------------- */

  function setPanelHeight(panel, height) {
    panel.style.height = height;
  }

  function clearPanelTransition(panel) {
    if (!panel || !panel.__tfxFaqOnEnd) return;
    panel.removeEventListener('transitionend', panel.__tfxFaqOnEnd);
    delete panel.__tfxFaqOnEnd;
  }

  function bindAutoHeight(panel) {
    clearPanelTransition(panel);

    var onEnd = function (e) {
      if (e.propertyName !== 'height') return;
      setPanelHeight(panel, 'auto');
      clearPanelTransition(panel);
    };

    panel.__tfxFaqOnEnd = onEnd;
    panel.addEventListener('transitionend', onEnd);
  }

  function bindClosedHeight(panel) {
    clearPanelTransition(panel);

    var onEnd = function (e) {
      if (e.propertyName !== 'height') return;
      clearPanelTransition(panel);
    };

    panel.__tfxFaqOnEnd = onEnd;
    panel.addEventListener('transitionend', onEnd);
  }

  function getMediaBaseHeight(root) {
    var rawValue = window.getComputedStyle(root).getPropertyValue('--tfx-faq-media-media-size');
    var baseHeight = parseFloat(rawValue);
    return Number.isFinite(baseHeight) ? baseHeight : 320;
  }

  function getMediaAvailableWidth(root) {
    var media = root.querySelector('.tfx-faq-media__media');
    var mediaWidth = media ? media.getBoundingClientRect().width : 420;
    return Math.max(0, Math.min(mediaWidth, 420));
  }

  function setMediaSize(root, size) {
    root.style.setProperty('--tfx-faq-media-media-target-size', Math.ceil(size) + 'px');
  }

  function setClosedMediaSize(root) {
    setMediaSize(root, getMediaBaseHeight(root));
  }

  function setOpenMediaSize(root, contentHeight) {
    var baseHeight = getMediaBaseHeight(root);
    var availableWidth = getMediaAvailableWidth(root);
    var maxSize = availableWidth || baseHeight;
    var minSize = Math.min(baseHeight, maxSize);
    var targetSize = Math.max(minSize, Math.min(Math.ceil(contentHeight), maxSize));

    setMediaSize(root, targetSize);
  }

  function getContentHeight(root) {
    var content = root ? root.querySelector(CONTENT_SELECTOR) : null;
    return content ? content.getBoundingClientRect().height : 0;
  }

  function getActiveTabPanel(root) {
    if (!root) return null;
    return root.querySelector(TAB_PANEL_SELECTOR + ':not([hidden])');
  }

  function getAccordionScope(root) {
    return getActiveTabPanel(root) || root;
  }

  function getTabs(root) {
    return Array.prototype.slice.call(root.querySelectorAll(TAB_SELECTOR));
  }

  function setItemExpandedState(item, isOpen) {
    if (!item) return;

    var panel = item.querySelector(PANEL_SELECTOR);
    var btn = item.querySelector(TOGGLE_SELECTOR);
    if (!panel || !btn) return;

    if (isOpen) {
      item.setAttribute('data-open', 'true');
      btn.setAttribute('aria-expanded', 'true');
      panel.setAttribute('aria-hidden', 'false');
      setPanelHeight(panel, 'auto');
    } else {
      item.removeAttribute('data-open');
      btn.setAttribute('aria-expanded', 'false');
      panel.setAttribute('aria-hidden', 'true');
      setPanelHeight(panel, '0px');
    }
  }

  function measureClosedTabPanelHeight(root, panel) {
    if (!root || !panel) return 0;

    var content = root.querySelector(CONTENT_SELECTOR);
    var measureWidth = panel.getBoundingClientRect().width
      || (content ? content.getBoundingClientRect().width : 0);
    if (!measureWidth) return 0;

    var clone = panel.cloneNode(true);
    clone.hidden = false;
    clone.style.position = 'absolute';
    clone.style.left = '0';
    clone.style.top = '0';
    clone.style.visibility = 'hidden';
    clone.style.pointerEvents = 'none';
    clone.style.width = Math.ceil(measureWidth) + 'px';
    clone.style.height = 'auto';
    clone.style.minHeight = '0';
    clone.style.zIndex = '-1';

    clone.querySelectorAll(ITEM_SELECTOR).forEach(function (item) {
      setItemExpandedState(item, false);
    });

    root.appendChild(clone);
    var height = clone.getBoundingClientRect().height;
    root.removeChild(clone);

    return Math.ceil(height);
  }

  function syncClosedTabPanelsHeight(root) {
    if (!root) return;

    var panels = root.querySelectorAll(TAB_PANEL_SELECTOR);
    if (!panels.length) {
      root.style.removeProperty('--tfx-faq-media-tab-panel-min-height');
      return;
    }

    var maxHeight = 0;
    panels.forEach(function (panel) {
      maxHeight = Math.max(maxHeight, measureClosedTabPanelHeight(root, panel));
    });

    if (maxHeight > 0) {
      root.style.setProperty('--tfx-faq-media-tab-panel-min-height', maxHeight + 'px');
    } else {
      root.style.removeProperty('--tfx-faq-media-tab-panel-min-height');
    }
  }

  function scheduleClosedTabPanelsHeightSync(root) {
    if (!root) return;
    if (root.__tfxFaqTabPanelsRaf) {
      window.cancelAnimationFrame(root.__tfxFaqTabPanelsRaf);
    }

    root.__tfxFaqTabPanelsRaf = window.requestAnimationFrame(function () {
      delete root.__tfxFaqTabPanelsRaf;
      syncClosedTabPanelsHeight(root);
    });
  }

  function closeAllItems(root) {
    if (!root) return;

    root.querySelectorAll(ITEM_SELECTOR + '[data-open="true"]').forEach(function (item) {
      closeItem(item);
    });
  }

  function activateTab(root, tabId, focusButton, resetAccordions) {
    if (!root || !tabId) return;

    var currentActiveTab = root.querySelector(TAB_SELECTOR + '[aria-selected="true"]');
    if (resetAccordions && currentActiveTab && currentActiveTab.id !== tabId) {
      closeAllItems(root);
    }

    var activeButton = null;

    root.querySelectorAll(TAB_SELECTOR).forEach(function (tab) {
      var isActive = tab.id === tabId;
      tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
      tab.setAttribute('tabindex', isActive ? '0' : '-1');

      if (isActive) {
        activeButton = tab;
      }
    });

    root.querySelectorAll(TAB_PANEL_SELECTOR).forEach(function (panel) {
      panel.hidden = panel.getAttribute('aria-labelledby') !== tabId;
    });

    if (focusButton && activeButton) {
      activeButton.focus();
    }

    syncRootState(root);
  }

  function syncRootState(root) {
    if (!root) return;

    var scope = getAccordionScope(root);
    var hasOpenItem = !!(scope && scope.querySelector(ITEM_SELECTOR + '[data-open="true"]'));
    if (hasOpenItem) {
      root.setAttribute('data-faq-open', 'true');
    } else {
      root.removeAttribute('data-faq-open');
    }
  }

  function openItem(item) {
    var panel = item.querySelector(PANEL_SELECTOR);
    if (!panel) return;
    var root = item.closest(ROOT_SELECTOR);

    clearPanelTransition(panel);
    item.setAttribute('data-open', 'true');

    var btn = item.querySelector(TOGGLE_SELECTOR);
    if (btn) btn.setAttribute('aria-expanded', 'true');
    panel.setAttribute('aria-hidden', 'false');

    if (prefersReducedMotion()) {
      setOpenMediaSize(root, getContentHeight(root) + panel.scrollHeight);
      setPanelHeight(panel, 'auto');
      syncRootState(root);
      return;
    }

    var currentPanelHeight = panel.getBoundingClientRect().height;
    var projectedContentHeight = getContentHeight(root) - currentPanelHeight + panel.scrollHeight;
    var targetHeight = panel.scrollHeight;

    setPanelHeight(panel, '0px');
    panel.offsetHeight; // force reflow
    setPanelHeight(panel, targetHeight + 'px');
    bindAutoHeight(panel);
    setOpenMediaSize(root, projectedContentHeight);
    syncRootState(root);
  }

  function closeItem(item, options) {
    var panel = item.querySelector(PANEL_SELECTOR);
    if (!panel) return;
    var root = item.closest(ROOT_SELECTOR);
    options = options || {};
    var suppressMediaResize = !!options.suppressMediaResize;
    var suppressRootSync = !!options.suppressRootSync;

    clearPanelTransition(panel);
    item.removeAttribute('data-open');
    if (root && !getAccordionScope(root).querySelector(ITEM_SELECTOR + '[data-open="true"]')) {
      root.removeAttribute('data-faq-open');
    }

    var btn = item.querySelector(TOGGLE_SELECTOR);
    if (btn) btn.setAttribute('aria-expanded', 'false');
    panel.setAttribute('aria-hidden', 'true');

    if (prefersReducedMotion()) {
      if (!suppressMediaResize) {
        setClosedMediaSize(root);
      }
      setPanelHeight(panel, '0px');
      if (!suppressRootSync) {
        syncRootState(root);
      }
      return;
    }

    if (!suppressMediaResize) {
      setClosedMediaSize(root);
    }
    setPanelHeight(panel, panel.scrollHeight + 'px');
    panel.offsetHeight; // force reflow
    setPanelHeight(panel, '0px');
    if (!suppressRootSync) {
      syncRootState(root);
    }
    bindClosedHeight(panel);
  }

  function closeSiblings(scope, exceptItem, options) {
    if (!scope) return;

    scope.querySelectorAll(ITEM_SELECTOR).forEach(function (item) {
      if (item !== exceptItem && item.hasAttribute('data-open')) {
        closeItem(item, options);
      }
    });
  }

  /* ----------------------------
     Init per section
  ---------------------------- */

  function initSection(root) {
    if (!root || root.__tfxFaqInit) return;
    root.__tfxFaqInit = true;

    root.querySelectorAll(ITEM_SELECTOR).forEach(function (item) {
      var panel = item.querySelector(PANEL_SELECTOR);
      var btn = item.querySelector(TOGGLE_SELECTOR);
      var isOpen = item.getAttribute('data-open') === 'true';

      if (!panel || !btn) return;

      setItemExpandedState(item, isOpen);
    });

    var activeTab = root.querySelector(TAB_SELECTOR + '[aria-selected="true"]') || root.querySelector(TAB_SELECTOR);
    if (activeTab) {
      activateTab(root, activeTab.id, false, false);
    }

    if (supportsResizeObserver) {
      var content = root.querySelector(CONTENT_SELECTOR);
      if (content) {
        root.__tfxFaqResizeObserver = new ResizeObserver(function () {
          scheduleClosedTabPanelsHeightSync(root);
        });
        root.__tfxFaqResizeObserver.observe(content);
      }
    }

    scheduleClosedTabPanelsHeightSync(root);
    syncRootState(root);
    if (root.hasAttribute('data-faq-open')) {
      setOpenMediaSize(root, getContentHeight(root));
    } else {
      setClosedMediaSize(root);
    }

    var handleClick = function (e) {
      var tab = e.target.closest(TAB_SELECTOR);
      if (tab && root.contains(tab)) {
        activateTab(root, tab.id, false, true);
        return;
      }

      var btn = e.target.closest(TOGGLE_SELECTOR);
      if (!btn || !root.contains(btn)) return;

      var item = btn.closest(ITEM_SELECTOR);
      if (!item) return;
      var accordion = btn.closest(ACCORDION_SELECTOR);

      var isOpen = item.getAttribute('data-open') === 'true';

      if (isOpen) {
        closeItem(item);
      } else {
        closeSiblings(accordion, item, {
          suppressMediaResize: true,
          suppressRootSync: true
        });
        openItem(item);
      }
    };

    var handleKeydown = function (e) {
      var activeElementTab = e.target.closest(TAB_SELECTOR);
      if (activeElementTab && root.contains(activeElementTab)) {
        var tabs = getTabs(root);
        var currentIndex = tabs.indexOf(activeElementTab);
        var targetIndex = -1;

        if (e.key === 'ArrowRight') targetIndex = currentIndex + 1;
        if (e.key === 'ArrowLeft') targetIndex = currentIndex - 1;
        if (e.key === 'Home') targetIndex = 0;
        if (e.key === 'End') targetIndex = tabs.length - 1;

        if (targetIndex !== -1 && tabs.length) {
          e.preventDefault();
          if (targetIndex < 0) targetIndex = tabs.length - 1;
          if (targetIndex >= tabs.length) targetIndex = 0;
          activateTab(root, tabs[targetIndex].id, true, true);
        }

        return;
      }

      if (e.key !== ' ' && e.key !== 'Spacebar') return;
      var btn = e.target.closest(TOGGLE_SELECTOR);
      if (btn && root.contains(btn)) {
        e.preventDefault();
        btn.click();
      }
    };

    root.__tfxFaqHandleClick = handleClick;
    root.__tfxFaqHandleKeydown = handleKeydown;
    root.addEventListener('click', handleClick);
    root.addEventListener('keydown', handleKeydown);
  }

  function destroySection(root) {
    if (!root || !root.__tfxFaqInit) return;

    root.querySelectorAll(PANEL_SELECTOR).forEach(clearPanelTransition);
    if (root.__tfxFaqTabPanelsRaf) {
      window.cancelAnimationFrame(root.__tfxFaqTabPanelsRaf);
      delete root.__tfxFaqTabPanelsRaf;
    }
    if (root.__tfxFaqResizeObserver) {
      root.__tfxFaqResizeObserver.disconnect();
      delete root.__tfxFaqResizeObserver;
    }
    if (root.__tfxFaqHandleClick) {
      root.removeEventListener('click', root.__tfxFaqHandleClick);
      delete root.__tfxFaqHandleClick;
    }
    if (root.__tfxFaqHandleKeydown) {
      root.removeEventListener('keydown', root.__tfxFaqHandleKeydown);
      delete root.__tfxFaqHandleKeydown;
    }
    delete root.__tfxFaqInit;
  }

  /* ----------------------------
     Global init
  ---------------------------- */

  function initAll(scope) {
    scope = scope || document;
    scope.querySelectorAll(ROOT_SELECTOR).forEach(initSection);
  }

  window[INIT_KEY] = initAll;

  document.addEventListener('shopify:section:load', function (e) {
    if (e.target) initAll(e.target);
  });

  document.addEventListener('shopify:section:select', function (e) {
    if (e.target) initAll(e.target);
  });

  document.addEventListener('shopify:section:unload', function (e) {
    if (!e.target) return;
    e.target.querySelectorAll(ROOT_SELECTOR).forEach(destroySection);
  });

  document.addEventListener('shopify:block:select', function (e) {
    if (!e.target) return;

    var selectedTab = e.target.closest(TAB_SELECTOR);
    if (selectedTab) {
      var tabRoot = selectedTab.closest(ROOT_SELECTOR);
      if (!tabRoot) return;
      initSection(tabRoot);
      activateTab(tabRoot, selectedTab.id, false, true);
      return;
    }

    var item = e.target.closest(ITEM_SELECTOR);
    if (!item) return;

    var root = item.closest(ROOT_SELECTOR);
    if (!root) return;

    initSection(root);

    var parentPanel = item.closest(TAB_PANEL_SELECTOR);
    if (parentPanel && parentPanel.hidden) {
      activateTab(root, parentPanel.getAttribute('aria-labelledby'), false, true);
    }

    if (item.getAttribute('data-open') !== 'true') {
      closeSiblings(item.closest(ACCORDION_SELECTOR), item, {
        suppressMediaResize: true,
        suppressRootSync: true
      });
      openItem(item);
    }
  });

  window.addEventListener('resize', function () {
    document.querySelectorAll(ROOT_SELECTOR).forEach(function (root) {
      scheduleClosedTabPanelsHeightSync(root);
    });
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); }, { once: true });
  } else {
    initAll();
  }
})();
